import { Component, Inject, OnInit } from '@angular/core'
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { CrudOps } from '../../../../api/constants/crud.enum'
import { Employee, EmployeePaginatedList } from '../../../../api/models/employee.model'
import { UserAccount } from '../../../../api/models/user-account.model'
import { AccountService } from '../../../../api/services/accounts.service'
import { LoaderService } from '../../../common/services/loader.service'
import { SnackbarService } from '../../../common/services/snackbar.service'
import { notUndefined } from '../../../common/utils/conditional-assert'
import { Utils } from '../../../common/utils/utils'
import { legitimateEmployeeValidator } from '../../../common/validators/validators'
import { RoleEnum } from '../../constants/roles.constant'

export interface AccountDialogData {
    crud: CrudOps
    userAccount: UserAccount
    employeeSubjectList: EmployeePaginatedList
}

@Component({
    selector: 'ppw-account-dialog',
    templateUrl: './account-dialog.component.html',
    styleUrls: ['./account-dialog.component.scss']
})
export class AccountDialogComponent implements OnInit {
    trackBy = Utils.trackByIndex
    dialogTitle!: string
    crud!: CrudOps
    accountFormGroup!: UntypedFormGroup
    employees!: Array<Employee>
    filteredEmployees!: Array<Employee>
    confirmDelete = false
    isEmployeesFound = true
    checkedName?: string

    private userAccount?: UserAccount
    private readonly prefix = 'AccountDialogComponent'
    private selectedEmployee?: Employee

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: AccountDialogData,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly dialogRef: MatDialogRef<AccountDialogComponent>,
        private readonly loaderService: LoaderService,
        private readonly accountService: AccountService,
        private readonly snackbarService: SnackbarService
    ) {}

    ngOnInit(): void {
        this.crud = this.data.crud
        this.userAccount = this.data.userAccount
        this.employees = [...this.data.employeeSubjectList.items].sort(Employee.fullNameCompareFn)
        this.filteredEmployees = this.employees
        this.initCheckedName()
        this.createAccountForm()
        this.crudHandler()
    }

    crudHandler(): void {
        switch (this.data.crud) {
            case CrudOps.Create:
                this.dialogTitle = 'New Account'
                break
            case CrudOps.Update:
                this.dialogTitle = 'Edit Account'
                break
            case CrudOps.Delete:
                this.dialogTitle = 'Delete Account'
                break
            default:
                this.dialogTitle = ''
        }
    }

    initCheckedName(): void {
        this.checkedName =
            this.userAccount && this.crud === CrudOps.Update ? this.userAccount.identityRoles[0] : undefined
    }

    onCheck(checkboxName: string): void {
        this.checkedName = checkboxName
    }

    employeeQuerySearch(input: string): void {
        this.filteredEmployees = this.employees.filter(
            (employee: Employee) =>
                employee && employee.fullName && employee.fullName.toLowerCase().includes(input.toLowerCase())
        )
        this.isEmployeesFound = this.filteredEmployees.length > 0
    }

    createAccountForm(): void {
        const employeeMatch: Employee | undefined = this.employees.find(
            (employee) => this.userAccount && employee.id === this.userAccount.employeeId
        )

        this.accountFormGroup = this.formBuilder.group(
            {
                userName: [
                    {
                        value: this.userAccount && this.userAccount.userName ? this.userAccount.userName : '',
                        disabled: this.crud === CrudOps.Update
                    },
                    [Validators.required, Validators.maxLength(40), Validators.email]
                ],
                firstName: [
                    this.userAccount && this.userAccount.firstName ? this.userAccount.firstName : '',
                    [Validators.required, Validators.maxLength(40)]
                ],
                lastName: [
                    this.userAccount && this.userAccount.lastName ? this.userAccount.lastName : '',
                    [Validators.required, Validators.maxLength(40)]
                ],
                employee: [
                    employeeMatch ? employeeMatch.fullName : '',
                    [
                        Validators.required,
                        Validators.maxLength(40),
                        legitimateEmployeeValidator(this.employees, this.setSelectedEmployee.bind(this))
                    ]
                ],
                roleRestricted: [
                    this.userAccount && this.userAccount.identityRoles
                        ? this.userAccount.identityRoles.includes(RoleEnum.RESTRICTED_USER)
                        : false
                ],
                roleUser: [
                    this.userAccount && this.userAccount.identityRoles
                        ? this.userAccount.identityRoles.includes(RoleEnum.TIMESHEET_USER)
                        : false
                ],
                roleAdmin: [
                    this.userAccount && this.userAccount.identityRoles
                        ? this.userAccount.identityRoles.includes(RoleEnum.ADMINISTRATOR)
                        : false
                ],
                roleImport: [
                    this.userAccount && this.userAccount.identityRoles
                        ? this.userAccount.identityRoles.includes(RoleEnum.IMPORTER)
                        : false
                ]
            },
            {
                validator: this.validateRoles
            }
        )

        if (this.crud === CrudOps.Delete) {
            this.accountFormGroup.disable()
        }
    }

    validateRoles(control: AbstractControl): ValidationErrors | null {
        const roleRestricted = control.get('roleRestricted')?.value
        const roleUser = control.get('roleUser')?.value
        const roleAdmin = control.get('roleAdmin')?.value
        const roleImport = control.get('roleImport')?.value

        if (!roleRestricted && !roleUser && !roleAdmin && !roleImport) {
            return { requiredRoles: true } // Return a validation error if no role is selected
        }

        return null // validation passed
    }

    setupUserAccount(): UserAccount {
        const selectedEmployee = notUndefined(this.selectedEmployee)
        const formValue = this.accountFormGroup.value
        const userAccount = {
            ...this.userAccount,
            userName: this.userAccount && this.userAccount.userName ? this.userAccount.userName : formValue.userName,
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            email: this.userAccount && this.userAccount.userName ? this.userAccount.userName : formValue.userName,
            employeeId: notUndefined(selectedEmployee.id),
            identityRoles: new Array<string>(),
            language: 'NL'
        }
        if (this.checkedName === RoleEnum.RESTRICTED_USER) {
            userAccount.identityRoles.push(RoleEnum.RESTRICTED_USER)
        }
        if (this.checkedName === RoleEnum.TIMESHEET_USER) {
            userAccount.identityRoles.push(RoleEnum.TIMESHEET_USER)
        }
        if (this.checkedName === RoleEnum.ADMINISTRATOR) {
            userAccount.identityRoles.push(RoleEnum.ADMINISTRATOR)
        }
        if (this.checkedName === RoleEnum.IMPORTER) {
            userAccount.identityRoles.push(RoleEnum.IMPORTER)
        }

        return userAccount
    }

    saveAccount(): void {
        this.loaderService.startLoading()
        this.userAccount = this.setupUserAccount()
        this.accountService
            .saveAccount(this.userAccount)
            .then((savedAccount: UserAccount) => {
                this.userAccount = savedAccount
                this.dialogRef.close(this.crud)
            })
            .catch((err: any) => {
                this.snackbarService.handleError(this.prefix, err, true)
            })
            .finally(() => {
                this.loaderService.stopLoading()
            })
    }

    deleteAccount(): void {
        if (this.confirmDelete) {
            this.loaderService.startLoading()
            this.accountService
                .deleteAccount(notUndefined(this.userAccount))
                .then(() => {
                    this.dialogRef.close(this.crud)
                })
                .catch((err: any) => {
                    this.snackbarService.handleError(this.prefix, err, true)
                })
                .finally(() => {
                    this.loaderService.stopLoading()
                })
        } else {
            this.confirmDelete = true
        }
    }

    cancelDialog(): void {
        this.dialogRef.close()
    }

    private setSelectedEmployee(employee: Employee): void {
        this.selectedEmployee = employee
    }
}
